import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  BookOpen, Clock, Award, CheckCircle, 
  ChevronRight, ArrowLeft, AlertCircle,
  GraduationCap
} from 'lucide-react';

// Helper function to format training content with proper HTML structure
const formatTrainingContent = (content) => {
  if (!content) return '';
  
  // Split content into lines
  const lines = content.split('\n');
  let html = '';
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    
    // Handle headings
    if (line.startsWith('### ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const headingText = line.substring(4);
      html += `<h3 class="text-lg font-semibold text-purple-800 mt-6 mb-3">${headingText}</h3>`;
      continue;
    }
    
    if (line.startsWith('## ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const headingText = line.substring(3);
      html += `<h2 class="text-xl font-bold text-purple-900 mt-8 mb-4">${headingText}</h2>`;
      continue;
    }
    
    // Handle bold text within lines
    line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-purple-900">$1</strong>');
    
    // Handle bullet points
    if (line.startsWith('- ')) {
      if (!inList) {
        html += '<ul class="list-disc pl-8 space-y-2 my-4">';
        inList = true;
      }
      const bulletText = line.substring(2);
      html += `<li class="text-gray-700">${bulletText}</li>`;
      continue;
    }
    
    // Handle numbered lists
    const numberedMatch = line.match(/^(\d+)\. (.*)/);
    if (numberedMatch) {
      if (!inList) {
        html += '<ol class="list-decimal pl-8 space-y-2 my-4">';
        inList = true;
      }
      html += `<li class="text-gray-700">${numberedMatch[2]}</li>`;
      continue;
    }
    
    // Handle blockquotes
    if (line.startsWith('> ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const quoteText = line.substring(2);
      html += `<blockquote class="border-l-4 border-purple-300 pl-4 italic text-gray-700 my-4">${quoteText}</blockquote>`;
      continue;
    }
    
    // Handle regular paragraphs
    if (line.trim()) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<p class="text-gray-700 mb-4 leading-relaxed">${line}</p>`;
    } else if (inList) {
      // Empty line ends the list
      html += '</ul>';
      inList = false;
    }
  }
  
  // Close any open list
  if (inList) {
    html += '</ul>';
  }
  
  return html;
};

export default function LMSTrainingCenter() {
  const { user } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const contentRef = useRef(null);

  // Scroll to top of content when section changes
  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigate to a section and scroll to top
  const goToSection = (sectionIndex) => {
    setCurrentSection(sectionIndex);
    setTimeout(scrollToTop, 100);
  };

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      const response = await api.get(`/lms/trainings?user_id=${user?.id || ''}`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Failed to load trainings:', error);
      // Try to seed trainings if none exist
      try {
        await api.post('/lms/seed-trainings');
        const retryResponse = await api.get(`/lms/trainings?user_id=${user?.id || ''}`);
        setTrainings(retryResponse.data);
      } catch (seedError) {
        toast.error('Failed to load trainings');
      }
    } finally {
      setLoading(false);
    }
  };

  const startTraining = async (trainingId) => {
    try {
      const response = await api.get(`/lms/trainings/${trainingId}`);
      setSelectedTraining(response.data);
      setCurrentSection(0);
      setShowQuiz(false);
      setQuizAnswers({});
      setQuizResult(null);
    } catch (error) {
      toast.error('Failed to load training content');
    }
  };

  const submitQuiz = async () => {
    if (Object.keys(quizAnswers).length < selectedTraining.questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setSubmittingQuiz(true);
    try {
      const response = await api.post(
        `/lms/trainings/${selectedTraining.id}/submit?employee_id=${user.id}`,
        { answers: quizAnswers }
      );
      setQuizResult(response.data);
      
      if (response.data.passed) {
        toast.success('Congratulations! You passed the training!');
        fetchTrainings(); // Refresh to show completion status
      } else {
        toast.error('You did not pass. Please review the material and try again.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetTraining = () => {
    setSelectedTraining(null);
    setCurrentSection(0);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizResult(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Training List View
  if (!selectedTraining) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-purple-100 rounded-xl">
            <GraduationCap className="w-8 h-8 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Training Center
            </h2>
            <p className="text-gray-600">Complete required trainings to maintain compliance</p>
          </div>
        </div>

        {/* Training Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {trainings.map((training) => (
            <Card 
              key={training.id} 
              className={`p-6 border-purple-100 hover:shadow-lg transition-all ${
                training.completed ? 'bg-green-50/50' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                </div>
                {training.completed ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Completed
                  </span>
                ) : training.is_required ? (
                  <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
                    <AlertCircle className="w-4 h-4" />
                    Required
                  </span>
                ) : null}
              </div>

              <h3 className="text-lg font-bold text-purple-900 mb-2">{training.title}</h3>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{training.description}</p>

              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {training.duration_minutes} min
                </span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                  {training.category}
                </span>
              </div>

              {training.completed && training.expires_at && (
                <p className="text-sm text-gray-600 mb-4">
                  Certificate expires: {new Date(training.expires_at).toLocaleDateString()}
                </p>
              )}

              <Button
                onClick={() => startTraining(training.id)}
                className={`w-full ${
                  training.completed 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {training.completed ? 'Review Training' : 'Start Training'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          ))}
        </div>

        {trainings.length === 0 && (
          <Card className="p-12 text-center border-purple-100">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-purple-50 rounded-full">
                <BookOpen className="w-12 h-12 text-purple-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-purple-900 mb-2">No trainings available</h3>
            <p className="text-gray-600">Check back soon for new training content.</p>
          </Card>
        )}
      </div>
    );
  }

  // Quiz Result View
  if (quizResult) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className={`p-8 text-center ${quizResult.passed ? 'border-green-200' : 'border-red-200'}`}>
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
            quizResult.passed ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {quizResult.passed ? (
              <Award className="w-10 h-10 text-green-600" />
            ) : (
              <AlertCircle className="w-10 h-10 text-red-600" />
            )}
          </div>

          <h2 className={`text-2xl font-bold mb-2 ${quizResult.passed ? 'text-green-900' : 'text-red-900'}`}>
            {quizResult.passed ? 'Congratulations!' : 'Not Quite There'}
          </h2>

          <p className="text-gray-600 mb-6">
            {quizResult.passed 
              ? 'You have successfully completed this training.'
              : 'Please review the material and try again.'}
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-purple-900">{Math.round(quizResult.score)}%</p>
                <p className="text-sm text-gray-600">Your Score</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-purple-900">
                  {quizResult.correct_answers}/{quizResult.total_questions}
                </p>
                <p className="text-sm text-gray-600">Correct Answers</p>
              </div>
            </div>
          </div>

          {quizResult.passed && quizResult.expires_at && (
            <p className="text-sm text-gray-600 mb-6">
              Your certificate is valid until {new Date(quizResult.expires_at).toLocaleDateString()}
            </p>
          )}

          <div className="flex gap-4 justify-center">
            <Button
              onClick={resetTraining}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Back to Trainings
            </Button>
            {!quizResult.passed && (
              <Button
                onClick={() => {
                  setQuizResult(null);
                  setShowQuiz(false);
                  setCurrentSection(0);
                  setQuizAnswers({});
                }}
                variant="outline"
                className="border-purple-600 text-purple-600"
              >
                Review Material
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Quiz View
  if (showQuiz) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Button
            onClick={() => setShowQuiz(false)}
            variant="ghost"
            className="text-purple-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Training
          </Button>
        </div>

        <Card className="p-6 border-purple-100">
          <h2 className="text-2xl font-bold text-purple-900 mb-2">Knowledge Assessment</h2>
          <p className="text-gray-600 mb-6">
            You need {selectedTraining.passing_score}% to pass. Answer all questions below.
          </p>

          <div className="space-y-6">
            {selectedTraining.questions.map((question, index) => (
              <div key={question.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 mb-4">
                  {index + 1}. {question.question}
                </p>
                <div className="space-y-2">
                  {['a', 'b', 'c', 'd'].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                        quizAnswers[question.id] === option
                          ? 'bg-purple-100 border-2 border-purple-500'
                          : 'bg-white border border-gray-200 hover:bg-purple-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={quizAnswers[question.id] === option}
                        onChange={() => setQuizAnswers(prev => ({ ...prev, [question.id]: option }))}
                        className="mr-3"
                      />
                      <span className="text-gray-700">{question[`option_${option}`]}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Answered: {Object.keys(quizAnswers).length} / {selectedTraining.questions.length}
            </p>
            <Button
              onClick={submitQuiz}
              disabled={submittingQuiz || Object.keys(quizAnswers).length < selectedTraining.questions.length}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {submittingQuiz ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Training Content View
  const currentSectionData = selectedTraining.sections[currentSection];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6" ref={contentRef}>
        <Button
          onClick={resetTraining}
          variant="ghost"
          className="text-purple-600 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to All Trainings
        </Button>

        <h1 className="text-2xl font-bold text-purple-900 mb-2">{selectedTraining.title}</h1>
        <p className="text-gray-600">{selectedTraining.description}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Section {currentSection + 1} of {selectedTraining.sections.length}</span>
          <span>{Math.round(((currentSection + 1) / selectedTraining.sections.length) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full transition-all"
            style={{ width: `${((currentSection + 1) / selectedTraining.sections.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Content */}
      <Card className="p-8 border-purple-100 mb-6">
        <h2 className="text-xl font-bold text-purple-900 mb-6">{currentSectionData.title}</h2>
        
        <div 
          className="prose prose-purple max-w-none training-content"
          dangerouslySetInnerHTML={{ 
            __html: formatTrainingContent(currentSectionData.content)
          }}
        />

        {currentSectionData.image_url && (
          <img 
            src={currentSectionData.image_url} 
            alt={currentSectionData.title}
            className="mt-6 rounded-lg max-w-full"
          />
        )}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          onClick={() => goToSection(currentSection - 1)}
          disabled={currentSection === 0}
          variant="outline"
          className="border-purple-600 text-purple-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous Section
        </Button>

        {currentSection < selectedTraining.sections.length - 1 ? (
          <Button
            onClick={() => goToSection(currentSection + 1)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Next Section
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={() => {
              setShowQuiz(true);
              setTimeout(scrollToTop, 100);
            }}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Take Quiz
            <Award className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Section Navigation */}
      <div className="mt-8">
        <p className="text-sm font-medium text-gray-600 mb-3">Jump to Section:</p>
        <div className="flex flex-wrap gap-2">
          {selectedTraining.sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => goToSection(index)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                index === currentSection
                  ? 'bg-purple-600 text-white'
                  : index < currentSection
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-purple-100'
              }`}
            >
              {index + 1}. {section.title.substring(0, 20)}...
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
